// ===================================================
// src/components/Planning/GeneratePlanModal.js
// ✅ Clean version — no unused vars, full M/L logic
// ===================================================
import React, { useState, useEffect } from 'react';
import { X, Target, Factory, Truck, Settings } from 'lucide-react';
import AdjustPercentagesModal from './AdjustPercentagesModal';

// ===================================================
// Helper: Generate forecast with M/L split
// ===================================================
function generateMonthlyPlan(formData) {
  const {
    start_date,
    end_date,
    total_sales,
    total_production,
    sales_percent,
    production_percent,
    ml_split,
    shipping_days
  } = formData;

  if (!start_date || !end_date) {
    alert('Please select both start and end dates');
    return [];
  }

  const start = new Date(start_date);
  const end = new Date(end_date);
  const numDays = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
  if (numDays <= 0) {
    alert('Invalid date range');
    return [];
  }

  const days = [];

  for (let i = 0; i < numDays; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);

    const fSales = {};
    const fProd = {};

    // 🧮 Loop through each main key (us_cityplay, eu_20, etc.)
    Object.keys(sales_percent).forEach(key => {
      const baseSales = total_sales * (sales_percent[key] / 100);
      const baseProd = total_production * (production_percent[key] / 100);
      const split = ml_split[key] || { m: 50, l: 50 };

      fSales[`${key}_m`] = baseSales * (split.m / 100);
      fSales[`${key}_l`] = baseSales * (split.l / 100);
      fProd[`${key}_m`] = baseProd * (split.m / 100);
      fProd[`${key}_l`] = baseProd * (split.l / 100);
    });

    // 🔧 fix rounding diff (keep exact total)
    const totalBeforeRound = Object.values(fSales).reduce((a, b) => a + b, 0);
    const roundSales = {};
    let diff = total_sales - Math.round(totalBeforeRound);

    Object.entries(fSales).forEach(([k, v]) => {
      roundSales[k] = Math.round(v);
    });

    // 🧮 recalc total and adjust one key for rounding offset
    const totalAfterRound = Object.values(roundSales).reduce((a, b) => a + b, 0);
    const fixKey = Object.keys(roundSales)[0];
    roundSales[fixKey] += total_sales - totalAfterRound;


    days.push({
      date: d.toISOString().split('T')[0],
      forecast: {
        sales: fSales,
        production: fProd,
        shipping: {
          us_days: shipping_days.us,
          eu_days: shipping_days.eu,
          uk_customer: shipping_days.uk_customer,
          us_customer: shipping_days.us_customer

        }
      }
    });
  }

  return days;
}

// ===================================================
// 🧠 GeneratePlanModal Component
// ===================================================
const GeneratePlanModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    total_sales: 1000,
    total_production: 2000,
    sales_percent: { us_cityplay: 40, us_20: 30, eu_cityplay: 20, eu_20: 10 },
    production_percent: { us_cityplay: 45, us_20: 25, eu_cityplay: 20, eu_20: 10 },
    ml_split: {
      us_cityplay: { m: 50, l: 50 },
      us_20: { m: 50, l: 50 },
      eu_cityplay: { m: 50, l: 50 },
      eu_20: { m: 50, l: 50 }
    },
    shipping_days: { us: 20, eu: 15, uk_customer: 5, us_customer: 7 }
  });

  const [showSettings, setShowSettings] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 🧩 Load default M/L split from DB
  useEffect(() => {
    if (isOpen) {
      (async () => {
        try {
          const res = await fetch('/api/plan/percentages/ml');
          const data = await res.json();
          if (data.success && data.ml_split) {
            setFormData(prev => ({
              ...prev,
              ml_split: data.ml_split
            }));
            console.log('✅ Loaded M/L percentages from DB');
          }
        } catch (err) {
          console.error('⚠️ Failed to load M/L percentages:', err);
        }
      })();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e, section, key) => {
    const value = e.target.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value;
    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: { ...prev[section], [key]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [key]: value }));
    }
  };

  // 💾 Submit plan
  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const forecastPlan = generateMonthlyPlan(formData);
    if (!forecastPlan.length) {
      setIsSubmitting(false);
      return;
    }

    try {
      const res = await fetch('/api/plan/forecast', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days: forecastPlan })
      });

      if (!res.ok) throw new Error(`Failed to create forecast plan (${res.status})`);

      alert('✅ Forecast plan saved successfully');
      onClose();
    } catch (err) {
      console.error('❌ Error saving forecast plan:', err);
      alert('❌ Failed to save forecast plan');
    } finally {
      setIsSubmitting(false);
    }
  };

  const salesSum = Object.values(formData.sales_percent).reduce((a, b) => a + b, 0);
  const prodSum = Object.values(formData.production_percent).reduce((a, b) => a + b, 0);

  // ===================================================
  // 💅 UI
  // ===================================================
  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={header}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>📅 Define Monthly Plan</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button
              onClick={() => setShowSettings(true)}
              style={{ background: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer', padding: '0.4rem 0.6rem' }}
            >
              <Settings size={18} color="#4b5563" />
            </button>
            <button onClick={onClose} style={closeBtn}><X size={20} color="#6b7280" /></button>
          </div>
        </div>

        {/* Dates */}
        <div style={grid2}>
          <div>
            <label>Start Date</label>
            <input type="date" style={input} value={formData.start_date} onChange={e => handleChange(e, null, 'start_date')} />
          </div>
          <div>
            <label>End Date</label>
            <input type="date" style={input} value={formData.end_date} onChange={e => handleChange(e, null, 'end_date')} />
          </div>
        </div>

        {/* Totals */}
        <div style={grid2}>
          <div>
            <label><Target size={14} /> Total Sales Target</label>
            <input type="number" style={input} value={formData.total_sales} onChange={e => handleChange(e, null, 'total_sales')} />
          </div>
          <div>
            <label><Factory size={14} /> Total Production Target</label>
            <input type="number" style={input} value={formData.total_production} onChange={e => handleChange(e, null, 'total_production')} />
          </div>
        </div>

        {/* Info */}
        <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
          Sales Split: {salesSum}% | Production Split: {prodSum}%
          <br />
          M/L ratios loaded from ⚙️ Adjust Percentages
        </p>

        {/* Shipping */}
        <div style={section}>
          <h4 style={sectionTitle}>🚚 Shipping Days</h4>
          <div style={grid2}>
            <div>
              <label><Truck size={14} /> US</label>
              <input type="number" style={input} value={formData.shipping_days.us} onChange={e => handleChange(e, 'shipping_days', 'us')} />
            </div>
            <div>
              <label><Truck size={14} /> EU</label>
              <input type="number" style={input} value={formData.shipping_days.eu} onChange={e => handleChange(e, 'shipping_days', 'eu')} />
            </div>
          </div>
        </div>

        {/* Submit */}
        <button
          type="button"
          onClick={handleSubmit}
          style={{ ...submitBtn, opacity: isSubmitting ? 0.6 : 1 }}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Generating…' : 'Generate Plan'}
        </button>

        {/* ⚙️ Adjust M/L Percentages */}
        {showSettings && (
          <AdjustPercentagesModal
            percentages={formData.ml_split}
            onSave={(updated) => {
              setFormData(prev => ({
                ...prev,
                ml_split: updated
              }));
            }}
            onClose={() => setShowSettings(false)}
          />
        )}
      </div>
    </div>
  );
};

// ===== Styles =====
const overlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 };
const modal = { background: 'white', padding: '1.5rem 2rem', borderRadius: '10px', width: '650px', boxShadow: '0 8px 25px rgba(0,0,0,0.15)', overflowY: 'auto', maxHeight: '90vh' };
const header = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.5rem' };
const closeBtn = { background: 'none', border: 'none', cursor: 'pointer' };
const section = { marginBottom: '1.25rem' };
const sectionTitle = { marginBottom: '0.5rem', fontWeight: 600, color: '#1f2937', fontSize: '1rem' };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' };
const input = { width: '100%', padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.875rem', outline: 'none', color: '#111827' };
const submitBtn = { marginTop: '1.5rem', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '6px', padding: '0.75rem 1.5rem', cursor: 'pointer', fontWeight: 600, fontSize: '1rem', width: '100%' };

export default GeneratePlanModal;
